import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Alarm, analogeMeting, cAfdelingInfo, cAfdelingWM, cKC_Afdeling, cKC_Alarm, cKC_Analog, cKC_BijzPrg, cKC_LosDef, cKC_LosDefs, cKC_Params, cKC_Servo, cKopConfig, cKopConfigModules, eKanaalType, JSBridge, KCParams, Losdef, sBijzPrg, Servo } from '@wasm/KopWeb';
import { JSBridgeService } from './jsbridge.service';
import { eraseFromWasmVector, wasmMapToRecord, wasmVectorFindIndex, wasmVectorForEach, wasmVectorToArray } from '../utils/wasmVector';
import { AnalogIn, AnalogOut, DigitalIn, DigitalOut, IOBaseType, IODirection, IOType, LosDefType, ModBusOut, ModuleAI, ModuleLosdef, ModuleServo, ModuleParam, ModuleBijzPrg, RangedKanaalAssign, KanaalUpdate, RangedKanaal, ModuleAlarm } from '../props/PropTypes';
import { KanaalService } from '../kanaal/kanaal.service';
import { MetaService } from './meta.service';


@Injectable({
  providedIn: null
})
export class KopConfigService {

  constructor(private jsBridgeService: JSBridgeService, private kanaalService: KanaalService, private metaService: MetaService) {
    jsBridgeService.getBridge().subscribe(bridge => {
      this.jsBridge = bridge;
      let cfg = this.jsBridge.getModuleConfig();
      this.config = cfg;
      let afd = wasmVectorToArray(cfg.afdelings);
      let isDirty = this.cleanLosDefs(afd);
      this.afdelings.set(afd);
      this.isDirty.set(isDirty);
      if (jsBridgeService.loadFromMemory) {
        this.isDirty.set(true);
      }
    });

    let cnt = 0;

    effect(async () => {
      this.selectedAfd();
      let bridge = this.jsBridgeService.getBridgeSync();
      if (bridge != undefined) {
        setTimeout(() => {
          let kfg = this.jsBridgeService.getBridgeSync().getConfig();
          let klantNo = this.jsBridgeService.getKlantNo();
          let assignement = this.kanaalService.getKonfigKanalen(kfg);
          localStorage.setItem(`kanaalAssign_${klantNo}`, JSON.stringify(assignement));
          cnt++;
          console.log("Count: " + cnt);
        }, 10);
      }
    })
  }

  public versionChanged() {
    this.afdelings.update(afd => {
      this.cleanLosDefs(afd);
      return afd;
    })
  }

  private cleanLosDefs(afdeligs: cAfdelingWM[]) {
    let isDirty = false;
    for (let afd of afdeligs) {
      isDirty = this.cleanLosDefsForAfdeling(afd, 'losdef') || isDirty;
      isDirty = this.cleanLosDefsForAfdeling(afd, 'droogwand') || isDirty;
      isDirty = this.cleanLosDefsForAfdeling(afd, 'koeling') || isDirty;
      isDirty = this.cleanLosDefsForAfdeling(afd, 'verwarming') || isDirty;
    }
    return isDirty;
  }

  private cleanLosDefsForAfdeling(afd: cAfdelingWM, type: LosDefType = 'losdef') {
    let ldRaw = this.metaService.afdelingMeta(afd?.afdeling()).losDefRaw;
    let ldsW = this.getAfdLosDefVector(afd, type);
    if (ldsW != undefined) {
      let erased: Array<number> = [];
      wasmVectorForEach(ldsW, (ld, idx) => {
        if (ldRaw.findIndex(ld2 => ld2.index == ld.index) < 0) {
          erased.push(ld.index);
        }
      });
      if (erased.length > 0) {
        let old = wasmVectorToArray(ldsW);
        ldsW.clear();
        for (let ld of old) {
          if (erased.findIndex(idx => idx == ld.index) < 0) {
            ldsW.push_back(ld);
          }
        }
        return true;
      }
    }
    return false;
  }

  private jsBridge: JSBridge | undefined;

  private config: cKopConfigModules | undefined;

  public afdelings = signal<Array<cAfdelingWM>>([], { equal: (a, b) => false });
  public selectedAfd = signal<cAfdelingWM | undefined>(undefined, { equal: (a, b) => false });
  public isDetail = signal<boolean>(false);
  public isDirty = signal<boolean>(false);

  public currentMeta = computed(() => {
    let afd = this.selectedAfd();
    this.metaService.cacheCleared();
    return this.metaService.afdelingMeta(afd?.afdeling());
  });

  public losDefTypeMap = computed(() => {
    let meta = this.currentMeta();
    let ret = new Map<number, LosDefType>();
    for (let ld of meta.losDefs) {
      ret.set(ld.index, 'losdef');
    }
    for (let ld of meta.drogWands) {
      ret.set(ld.index, 'droogwand');
    }
    for (let ld of meta.koelings) {
      ret.set(ld.index, 'koeling');
    }
    for (let ld of meta.verwarmings) {
      ret.set(ld.index, 'verwarming');
    }
    return ret;
  });


  refresh(updateState: boolean = true) {
    this.afdelings.update(afds => {
      if (this.config != undefined) {
        let afd = wasmVectorToArray(this.config.afdelings);
        return afd;
      }
      return afds;
    });
    if (updateState) {
      this.selectedAfd.update(afd => {
        return afd;
      });
      this.isDirty.set(true);
    }
  }

  public selectAfdeling(refnr: number) {
    this.selectedAfd.set(this.afdelings().find(afd => afd.afdeling().refNr == refnr));
  }

  applyAfdUpdate(updateFn: (value: cAfdelingWM | undefined) => cAfdelingWM | undefined) {
    this.selectedAfd.update(updateFn);
    this.isDirty.set(true);
  }

  addAfd(afd: cAfdelingInfo, title: string = "") {
    let a = this.config?.addAfd(afd.type);
    if (a != undefined) {
      a.titel = title;
    }
    this.refresh();
  }

  moveAfd(from: number, after: number) {
    this.config?.moveAfd(from, after);
    this.refresh();
  }


  buildUpdates(kanalen: RangedKanaalAssign[], type: IOType, offset: number) {
    let updates = kanalen.filter(k => k.rangedKnaal.ioType.equals(type) && k.rangedKnaal.start > 0).map(k => {
      let ret: KanaalUpdate = {
        ranged: k.rangedKnaal,
        offset: offset
      };
      return ret;
    });

    return updates;
  }

  public reNumberKanalen(oldKanalen: RangedKanaalAssign[], kanalen: RangedKanaalAssign[], newAfd: cKC_Afdeling, type: IOType, offset: number) {
    let updates = this.buildUpdates(kanalen, type, offset);
    this.kanaalService.applyAfdUpdates(newAfd, updates, false);
  }


  addAfdFromXML(xmlStr: string) {

    let newAfd = this.config?.addAfdFromXML(xmlStr);
    this.isDirty.set(true);
    return newAfd;
  }

  removeAfd(afd: cAfdelingWM | number) {
    let afdnr: number = 0;
    if (typeof afd == 'number') {
      afdnr = afd;
    } else {
      afdnr = afd.afdeling().refNr;
    }
    this.config?.removeAfd(afdnr);
    this.refresh();
    this.selectedAfd.set(undefined);
  }

  getAfdLosDefVector(afd: cAfdelingWM, ldType: LosDefType = 'losdef'): cKC_LosDefs | undefined {
    let ldsW: cKC_LosDefs | undefined = undefined;
    switch (ldType) {
      case 'losdef':
        ldsW = afd.afdeling().losDefs;
        break;
      case 'droogwand':
        ldsW = afd.afdeling().droogWand;
        break;
      case 'koeling':
        ldsW = afd.afdeling().koeling;
        break;
      case 'verwarming':
        ldsW = afd.afdeling().verwarming;
        break;
    }

    return ldsW;

  }


  findKCLosDef(meta: Losdef | number, ldType: LosDefType = 'losdef'): cKC_LosDef | undefined {
    let oMeta = meta;
    if (typeof meta == 'number') {
      meta = this.currentMeta().losDefRaw.find(s => s.index == meta)!;
    }
    if (meta == undefined) {
      return undefined;
    }
    let afd = this.selectedAfd()!;
    let ld = undefined;
    let ldsW = this.getAfdLosDefVector(afd, ldType);
    if (ldsW != undefined) {
      let idx = wasmVectorFindIndex(ldsW, ld => ld.index == meta.index);//lds.findIndex(ld => ld.index == meta.index);
      if (idx >= 0) {
        ld = ldsW.at(idx);
      }
    }

    return ld;

  }

  findLDRange(meta: Losdef, ldType: LosDefType = 'losdef'): number {
    if (meta.koppelindx > 0) {
      let ld = this.findKCLosDef(meta.koppelindx, ldType);
      if (ld != undefined) {
        return ld.waarde;
      }
    }
    return meta.range;
  }
  findOrBuildLosDef(meta: Losdef | number, ldType: LosDefType = 'losdef'): ModuleLosdef {
    if (typeof meta == 'number') {
      let tmMeta = this.currentMeta().losDefRaw.find(s => s.index == meta);
      if (tmMeta == undefined) {
        console.log('!!!Could not find losdef: ' + meta);
      }
      meta = tmMeta!;
    }


    let ld = this.findKCLosDef(meta, ldType);
    if (ld == undefined) {
      return {
        index: meta.index,
        type: meta.type,
        waarde: undefined,
        inverted: false,
        range: this.findLDRange(meta, ldType)
      }
    }
    let type = this.kanaalService.getIOTypeFromParamType(ld.type);
    let inverted = false;
    if (type != undefined) {
      inverted = this.kanaalService.getInvTab(ld.waarde, type);
    }
    return {
      index: ld.index,
      type: ld.type,
      waarde: ld.waarde,
      inverted: inverted,
      range: ld.range
    }
  }

  eraseAI(afd: cAfdelingWM | undefined, aim: ModuleAI | number) {
    let idx = 0;
    if (typeof aim == 'number') {
      idx = aim;
    } else {
      idx = aim.index;
    }
    if (afd != undefined) {
      let aiW = afd.afdeling().analogs;
      let old = wasmVectorToArray(aiW);
      aiW.clear();
      for (let ai of old) {
        if (ai.index != idx) {
          aiW.push_back(ai);
        }
      }
    }
  }


  eraseServo(afd: cAfdelingWM | undefined, servo: ModuleServo | number) {
    let idx = 0;
    if (typeof servo == 'number') {
      idx = servo;
    } else {
      idx = servo.index;
    }
    if (afd != undefined) {
      let servoW = afd.afdeling().servos;
      let old = wasmVectorToArray(servoW);
      servoW.clear();
      for (let s of old) {
        if (s.index != idx) {
          servoW.push_back(s);
        }
      }
    }
  }

  eraseBijzPrgm(prgm: ModuleBijzPrg | number) {
    let idx = 0;
    if (typeof prgm == 'number') {
      idx = prgm;
    } else {
      idx = prgm.index;
    }
    this.applyAfdUpdate(afd => {
      if (afd != undefined) {
        let bijzPrgs = afd.afdeling().bijzPrgs;
        let old = wasmVectorToArray(afd.afdeling().bijzPrgs);
        bijzPrgs.clear();
        for (let s of old) {
          if (s.index != idx) {
            bijzPrgs.push_back(s);
          }
        }
      }
      return afd;
    });
  }


  upsertAlarm(editVal: ModuleAlarm) {
    if (editVal.kanaal == 0) {
      this.eraseAlarm(editVal.index);
      return;
    }
    this.applyAfdUpdate(afd => {
      this.kanaalService.setInvTab(editVal.kanaal, DigitalIn, editVal.inverted, 1);
      let alarms = afd?.afdeling().alarms;
      if (alarms != undefined) {
        let idx = wasmVectorToArray(alarms).findIndex(al => al.index == editVal.index);
        let alarmRef: cKC_Alarm | undefined;
        if (idx >= 0) {
          alarmRef = alarms.at(idx);
        } else {
          let wasm = this.jsBridgeService.wasmModule!;
          alarmRef = new wasm.cKC_Alarm();
        }
        if (alarmRef != undefined) {
          alarmRef.index = editVal.index;
          alarmRef.kanaal = editVal.kanaal;
          alarmRef.typeIndex = editVal.typeIndex;
        }
        if (idx < 0) {
          alarms.push_back(alarmRef);
        }
      }

      return afd;
    });

  }
  eraseAlarm(alarm: ModuleAlarm | number) {
    let idx = 0;
    if (typeof alarm == 'number') {
      idx = alarm;
    } else {
      idx = alarm.index;
    }
    this.applyAfdUpdate(afd => {
      let alarmsW = afd?.afdeling().alarms;
      if (alarmsW != undefined) {
        eraseFromWasmVector(alarmsW, idx);
      }
      return afd;
    });
  }

  eraseLosDef(afd: cAfdelingWM | undefined, ldm: ModuleLosdef | number, ldType: LosDefType = 'losdef') {
    let idx = 0;
    if (typeof ldm == 'number') {
      idx = ldm;
    } else {
      idx = ldm.index;
    }
    if (afd != undefined) {
      let ldsW = this.getAfdLosDefVector(afd, ldType);
      if (ldsW != undefined) {
        let old = wasmVectorToArray(ldsW);
        ldsW.clear();
        for (let ld of old) {
          if (ld.index != idx) {
            ldsW.push_back(ld);
          }
        }
      }
    }
  }

  upsertLosDef(ldm: ModuleLosdef, ldType: LosDefType = 'losdef') {
    let meta = this.currentMeta().losDefRaw.find(s => s.index == ldm.index)!;
    this.applyAfdUpdate(afd => {
      if (ldm.waarde == undefined || (ldm.waarde == 0 && meta.menunr == 0)) {
        this.eraseLosDef(afd, ldm, ldType);
        return afd;
      }
      let ld = this.findKCLosDef(ldm.index, ldType);
      let editVal = ldm;
      let toInsert = false;
      if (ld == undefined) {
        let wasm = this.jsBridgeService.wasmModule;
        if (wasm != undefined) {
          ld = new wasm.cKC_LosDef();
          ld.index = editVal.index;
          ld.type = editVal.type;
          toInsert = true;
        }
      }
      if (ld != undefined) {
        if (editVal.waarde != undefined) {
          ld.waarde = editVal.waarde;
          if (editVal.range != undefined) {
            ld.range = editVal.range;
          }
          let kanaalType = this.kanaalService.getIOTypeFromParamType(ld.type);
          if (kanaalType != undefined) {
            this.kanaalService.setInvTab(editVal.waarde, kanaalType, editVal.inverted == true, ld.range);
          }
          if (AnalogOut.equals(kanaalType)) {
            this.kanaalService.setAOV(editVal.waarde, editVal.inverted == true);
          }
          //Range LosDef
          if (ld.type & 0x40) {
            let koppelMetas = this.currentMeta().losDefRaw.filter(s => s.koppelindx == ld.index)!;
            for (let kp of koppelMetas) {
              let ld = this.findKCLosDef(kp.index, ldType);
              if (ld != undefined) {
                ld.range = editVal.waarde;
              }
            }
          }
        }
      }

      if (toInsert && ld != undefined) {
        switch (ldType) {
          case 'losdef':
            afd?.afdeling().losDefs.push_back(ld);
            break;
          case 'droogwand':
            afd?.afdeling().droogWand.push_back(ld);
            break;
          case 'koeling':
            afd?.afdeling().koeling.push_back(ld);
            break;
          case 'verwarming':
            afd?.afdeling().verwarming.push_back(ld);
            break;
        }

      }
      return afd;
    });
  }
  aiKCtoModule(meta: analogeMeting | number, ai: cKC_Analog | undefined): ModuleAI {
    if (typeof meta == 'number') {
      meta = this.currentMeta().ais.find(s => s.index == meta)!;
    }
    if (ai == undefined) {
      let groups = this.metaService.aiTypes().findByGroup(meta.typeGroep);
      let typeIndex = 0;
      if (groups != undefined && groups.length > 0) {
        typeIndex = groups[0].index;
      } else {
        console.log('AI met meta niet gevonden', meta);
      }
      let ret = {
        index: meta.index,
        kanaal: 0,
        range: meta.kanalen,
        typeIndex: typeIndex
      }
      return ret;

    }
    return {
      index: ai.index,
      kanaal: ai.kanaal,
      range: ai.range,
      typeIndex: ai.typeIndex
    };
  }

  kcParamsoModule(params: cKC_Params): Record<number, ModuleParam> {
    var paramKeys = wasmVectorToArray(params.keys());
    var moduleParams: Record<number, ModuleParam> = [];
    for (var pNo of paramKeys) {
      var param = params.get(pNo);
      if (param != undefined) {
        let rk = this.kanaalService.getKCParamType({
          waarde: param.waarde,
          range: param.range,
          type: param.type
        });
        let inverted = false;
        if (rk != undefined) {
          inverted = this.kanaalService.getInvTab(rk.start, rk.ioType);
        }
        moduleParams[pNo] = {
          waarde: param.waarde,
          range: param.range,
          type: param.type,
          inverted
        };
      }
    }

    return moduleParams;
  }

  number2timerange(tijd: number) {
    let h = Math.floor(tijd / 60);
    let m = tijd % 60;
    return '' + h.toString().padStart(2, '0') + m.toString().padStart(2, '0');

  }

  timerange2number(tijd: string) {
    console.log(tijd);
    let pt = tijd.padStart(4, '0');
    let h = parseInt(pt.substring(0, 2));
    let m = parseInt(pt.substring(2, 4));
    return h * 60 + m;
  }
  servoKCtoModule(meta: Servo | number, servo: cKC_Servo | undefined): ModuleServo {
    if (servo == undefined) {
      return this.buildDefaultServo(meta);
    }

    var servoParams = this.kcParamsoModule(servo.params);

    let kanaalType = this.kanaalService.getServoKanaalType({ soort: servo.soort.value, uitgang: servo.uitgang, stationnr: servo.stationnr, modbusNetworkId: servo.modbusNetworkId });
    let inverted = false;
    if (kanaalType != undefined) {
      inverted = this.kanaalService.getInvTab(servo.uitgang, kanaalType.ioType);
    }
    let volt = false;
    if (AnalogOut.equals(kanaalType?.ioType)) {
      volt = this.kanaalService.getAOV(servo.uitgang);
    }
    let ret: ModuleServo = {
      index: servo.index,
      looptijd: this.number2timerange(servo.looptijd),
      modbusNetworkId: servo.modbusNetworkId,
      uitgang: servo.uitgang,
      soort: servo.soort.value,
      stationnr: servo.stationnr,
      inverted: inverted,
      volt: volt,
      params: servoParams
    };

    return ret;

  }

  bijzPrgmKCtoModule(meta: sBijzPrg | number, bijz: cKC_BijzPrg | undefined): ModuleBijzPrg {
    if (bijz == undefined) {
      return this.buildDefaultBijzPrgm(meta);
    }
    var bijzParams = this.kcParamsoModule(bijz.params);

    let ret = {
      index: bijz.index,
      params: bijzParams
    }

    return ret;
  }

  buildDefaultBijzPrgm(meta: sBijzPrg | number): ModuleBijzPrg {
    if (typeof meta == 'number') {
      meta = this.currentMeta().bijzPrgs.find(s => s.index == meta)!;
    }
    let params = this.buildDefaultCKParams(meta.param);
    let ret = {
      index: meta.index,
      params: params
    };

    return ret;

  }

  buildDefaultCKParams(metaParams: KCParams): Record<number, ModuleParam> {
    let ret: Record<number, ModuleParam> = {};
    for (let i = 0; i < metaParams.size(); i++) {
      let v = metaParams.get(i);
      if (v != undefined) {
        let msp: ModuleParam = {
          inverted: false,
          range: v.range,
          type: v.type,
          waarde: 0
        };
        ret[v.no] = msp;
      }
    }
    return ret;
  }

  buildDefaultServo(meta: Servo | number): ModuleServo {
    if (typeof meta == 'number') {
      meta = this.currentMeta().servos.find(s => s.index == meta)!;
    }
    let params = this.buildDefaultCKParams(meta.param);
    let ret = {
      index: meta.index,
      uitgang: 0,
      inverted: false,
      volt: false,
      looptijd: '00:00',
      modbusNetworkId: 0,
      stationnr: 0,
      soort: 0,
      params: params
    };

    return ret;

  }


  upsertAI(aim: ModuleAI) {
    let editValue = aim;
    this.applyAfdUpdate((afd) => {
      if (afd != undefined) {
        if (aim.kanaal == 0) {
          this.eraseAI(afd, aim);

        } else {
          var ais = wasmVectorToArray(afd.afdeling().analogs);
          let aiIdx = ais.findIndex(s => s.index == aim.index);
          let wasm = this.jsBridgeService.wasmModule!;
          let ai: cKC_Analog | undefined = undefined;
          if (aiIdx < 0) {
            ai = new wasm.cKC_Analog();
            ai.index = editValue.index;
          } else {
            ai = afd.afdeling().analogs.at(aiIdx);
          }
          ai.kanaal = editValue.kanaal;
          ai.typeIndex = editValue.typeIndex;
          ai.range = editValue.range;
          if (aiIdx < 0) {
            afd.afdeling().analogs.push_back(ai);
          }

        }
      }
      return afd;
    });

  }

  upsertKCParams(ckPrams: cKC_Params, moduleParams: Record<number, ModuleParam>) {
    let paramKeys = Object.entries(moduleParams).map(e => parseInt(e[0]));
    let oldParamKeys = new Set(wasmVectorToArray(ckPrams.keys()));
    let wasm = this.jsBridgeService.wasmModule!;
    for (var pNo of paramKeys) {
      var newParam = moduleParams[pNo];
      let kanaalType = this.kanaalService.getKCParamType(newParam);
      if (kanaalType != undefined) {
        this.kanaalService.setInvTab(newParam.waarde, kanaalType.ioType, newParam.inverted, kanaalType.range);
      }

      if (oldParamKeys.has(pNo)) {
        var kcParam = ckPrams.at(pNo);
        if ((kcParam != undefined) && (newParam != undefined)) {
          kcParam.waarde = newParam.waarde;
        }
      } else {
        let p = new wasm.cKC_Param();
        p.range = newParam.range;
        p.type = newParam.type;
        p.waarde = newParam.waarde;
        ckPrams.set(pNo, p);
      }
    }

  }

  buildKCParams(metaParam: KCParams): cKC_Params {
    let wasm = this.jsBridgeService.wasmModule!;
    let ret = new wasm.cKC_Params();
    for (let i = 0; i < metaParam.size(); i++) {
      let mp = metaParam.get(i)!;
      let key = mp.no;
      let p = new wasm.cKC_Param();
      p.range = mp.range;
      p.type = mp.type;
      // p.waarde  = 0;
      ret.set(key, p);
    }

    return ret;

  }
  upsertBijzPrgm(bijz: ModuleBijzPrg, meta: sBijzPrg) {
    let editValue = bijz;
    this.applyAfdUpdate((afd) => {
      if (afd != undefined) {
        let wasm = this.jsBridgeService.wasmModule!;
        var bijzPrgs = wasmVectorToArray(afd.afdeling().bijzPrgs);
        let bpIdx = bijzPrgs.findIndex(s => s.index == editValue.index);
        let bp: cKC_BijzPrg | undefined = undefined;
        if (bpIdx < 0) {
          bp = new wasm.cKC_BijzPrg();
          bp.index = editValue.index;
          bp.params = this.buildKCParams(meta.param);
          afd.afdeling().bijzPrgs.push_back(bp);
          bpIdx = afd.afdeling().bijzPrgs.size() - 1;
        }
        bp = afd.afdeling().bijzPrgs.at(bpIdx);
        if (bp != undefined) {
          this.upsertKCParams(bp.params, editValue.params);
        }
      }
      return afd;
    });
  }
  upsertServo(sm: ModuleServo, meta: Servo) {
    let editValue = sm;
    this.applyAfdUpdate((afd) => {
      if (afd != undefined) {
        if ((sm.soort != 6 && sm.uitgang == 0) || (sm.soort == 6 && sm.stationnr == 0)) {
          this.eraseServo(afd, sm);
          return afd;
        }
        var servos = wasmVectorToArray(afd.afdeling().servos);
        let servoIdx = servos.findIndex(s => s.index == editValue.index);
        let servo: cKC_Servo | undefined = undefined;
        let wasm = this.jsBridgeService.wasmModule!;
        let soortMap = Object.fromEntries(Object.entries(wasm.eServoSoort).map((v) => {
          return [v[1].value, v[1]];
        }));
        if (servoIdx < 0) {
          servo = new wasm.cKC_Servo();
          servo.index = editValue.index;
          servo.params = this.buildKCParams(meta.param);

        } else {
          servo = afd.afdeling().servos.at(servoIdx);
        }
        if (servo != undefined) {
          servo.uitgang = editValue.uitgang;
          servo.stationnr = editValue.stationnr;
          if (editValue.soort == 6) { //Bij Servo uitgang moet niet null zijn
            servo.uitgang = Math.max(editValue.stationnr, 1);
          }
          servo.modbusNetworkId = editValue.modbusNetworkId;
          servo.soort = soortMap[editValue.soort];
          servo.looptijd = this.timerange2number(editValue.looptijd);
          let kanaalType = this.kanaalService.getServoKanaalType(editValue);
          if (kanaalType != undefined) {
            this.kanaalService.setInvTab(editValue.uitgang, kanaalType.ioType,
              editValue.inverted, kanaalType.range);
          }
          if (AnalogOut.equals(kanaalType?.ioType)) {
            this.kanaalService.setAOV(editValue.uitgang, editValue.volt);
          }
          this.upsertKCParams(servo.params, editValue.params);
        }
        if (servoIdx < 0 && servo != undefined) {
          afd.afdeling().servos.push_back(servo);
        }

      }
      return afd;
    });

  }

  getTextForAlarm(meta: Alarm | undefined) {
    if (meta == undefined) return "";
    let kopConfig = this.jsBridgeService.kopConfig();
    if (kopConfig != undefined) {
      let globalAlarmTeksten = wasmMapToRecord<string>(kopConfig.gAlarmTeksten, (e: any) => e.toString());
      let afdTeksten = wasmMapToRecord(this.selectedAfd()?.afdeling()?.alarmTexts, (e: any) => e.toString());
      Object.entries(afdTeksten).forEach(([key, val]) => {
        globalAlarmTeksten[parseInt(key) + 20] = val;
      });
      if (meta.vrijetext == 0) return meta.omschr.toString();
      let vrijText = globalAlarmTeksten[meta.vrijetext - 1] ?? '';
      return `${vrijText} - V(${meta.vrijetext})`;
    }
    return "";
  }


  getAlarmsMeta() {
    let afd = this.selectedAfd()?.afdeling()!
    let jsBridge = this.jsBridgeService.getBridgeSync();
    let alarmsMeta = wasmVectorToArray(jsBridge.getAlarms(afd.type.value))!;
    return alarmsMeta;
  }

  getAlarmMeta(a: { index: number } | undefined) {
    if (a == undefined) return undefined;
    let meta = this.getAlarmsMeta().find(al => al.index == a.index);
    return meta;

  }

  kcAlarm2Model(idx: number, alarm: cKC_Alarm | undefined) {
    if (alarm == undefined) {
      let ret = this.buildAlarmModule(idx);
      return ret;
    }
    let inv = this.kanaalService.getInvTab(alarm.kanaal, DigitalIn);
    let ret = {
      index: alarm.index,
      kanaal: alarm.kanaal,
      typeIndex: alarm.typeIndex,
      inverted: inv
    }
    return ret;
  }

  getAlarmTypes(meta: Alarm) {
    let jsBridge = this.jsBridgeService.getBridgeSync();
    let alarmTypes = wasmVectorToArray(jsBridge.getAlarmTypes()).filter(at => at.groep == meta?.groep);
    return alarmTypes.map(at => {
      return {
        index: at.index,
        text: at.omschr
      }
    });

  }

  buildAlarmModule(idx: number) {
    let alarm = this.getAlarmMeta({ index: idx });
    if (alarm == undefined) return;
    if (alarm.vrijetext == 0) {
      return {
        index: alarm.index,
        kanaal: 0,
        typeIndex: 0,
        inverted: false
      }
    } else {
      let alarmTypes = this.getAlarmTypes(alarm);
      return {
        index: alarm.index,
        kanaal: 0,
        typeIndex: alarmTypes[0].index,
        inverted: false
      }
    }

  }
}
